import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { api } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft, Plus, X, Save, Edit2, Trash2, Image as ImageIcon, 
  Building2, MapPin, DollarSign, Percent, Calendar, TrendingUp,
  Users, FileText, Clock, CheckCircle, AlertTriangle, Star,
  Home, Bed, Bath, Ruler, Layers, CalendarDays, Link as LinkIcon,
  Shield, Target, PieChart, BarChart3, Settings, Eye, Send,
  Briefcase, Building, Car, Thermometer, Mountain, Waves,
  GripVertical, ChevronUp, ChevronDown, ExternalLink, Search, Unlink,
  Info, Banknote, Landmark
} from 'lucide-react';
import { toast } from 'sonner';
import NavManagement from '../../components/admin/NavManagement';
import ProjectWorkspaceHeader from '../../components/admin/project/ProjectWorkspaceHeader';
import ProjectOverviewSummary from '../../components/admin/project/ProjectOverviewSummary';
import ProjectUpdatesTab from '../../components/admin/project/ProjectUpdatesTab';
import ProjectStatusWorkflow from '../../components/admin/project/ProjectStatusWorkflow';
import ProjectExitResultsPanel from '../../components/admin/project/ProjectExitResultsPanel';

const PROJECT_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'details', label: 'Project Details' },
  { value: 'assets', label: 'Assets & Property' },
  { value: 'instruments', label: 'Investment' },
  { value: 'financials', label: 'Financials' },
  { value: 'sponsor', label: 'Sponsor/Developer' },
  { value: 'images', label: 'Images' },
  { value: 'updates', label: 'Updates' },
  { value: 'workflow', label: 'Workflow' },
];
import { InstrumentCard } from '../../components/admin/instruments/InstrumentCard';
import { InstrumentFormDialog } from '../../components/admin/instruments/InstrumentFormDialog';
import { FinanceEntryFormDialog } from '../../components/admin/finance/FinanceEntryFormDialog';
import { ProjectFinancialsWorkspace } from '../../components/admin/finance/ProjectFinancialsWorkspace';
import { phaseMeta, UNASSIGNED_PHASE } from '../../lib/projectPhases';
import { developerParties, issuerParties } from '../../lib/partyRoles';
import { PROJECT_TYPES } from '../../lib/projectTypes';

// ============================================================================
// CONSTANTS & CONFIGURATIONS
// ============================================================================

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
  pending_review: { label: 'In Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  published: { label: 'Published', color: 'bg-green-100 text-green-700', icon: Eye },
  funding: { label: 'Funding', color: 'bg-emerald-100 text-emerald-700', icon: TrendingUp },
  funded: { label: 'Funded', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  in_execution: { label: 'In Execution', color: 'bg-indigo-100 text-indigo-700', icon: Building2 },
  operating: { label: 'Operating', color: 'bg-teal-100 text-teal-700', icon: Settings },
  exiting: { label: 'Exiting', color: 'bg-orange-100 text-orange-700', icon: Clock },
  exited: { label: 'Exited', color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-500', icon: FileText },
};

// Strategy and project type are the same list - see lib/projectTypes.js
const strategyOptions = PROJECT_TYPES.map(({ value, label }) => ({ value, label }));

// From data dictionary: stage enum
const stageOptions = [
  { value: 'review', label: 'Review' },
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'permitting', label: 'Permitting' },
  { value: 'construction', label: 'Construction' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'operating', label: 'Operating' },
  { value: 'exited', label: 'Exited' },
  { value: 'cancelled', label: 'Cancelled' },
];

// From data dictionary: developer_relationship enum
const developerRelationshipOptions = [
  { value: 'flippex_led', label: 'Flippex Led' },
  { value: 'third_party_led', label: 'Third Party Led' },
  { value: 'joint_venture', label: 'Joint Venture' },
];

// From data dictionary: property_use enum
const propertyUseOptions = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'mixed_use', label: 'Mixed Use' },
  { value: 'land', label: 'Land' },
  { value: 'other', label: 'Other' },
];

// From data dictionary: property_form enum
const propertyFormOptions = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'house', label: 'House' },
  { value: 'building', label: 'Building' },
  { value: 'commercial_unit', label: 'Commercial Unit' },
  { value: 'plot', label: 'Plot' },
  { value: 'other', label: 'Other' },
];

// From data dictionary: condition enum
const conditionOptions = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'needs_renovation', label: 'Needs Renovation' },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'shell', label: 'Shell' },
  { value: 'derelict', label: 'Derelict' },
  { value: 'not_applicable', label: 'Not Applicable' },
];

// From data dictionary: occupancy enum
const occupancyOptions = [
  { value: 'vacant', label: 'Vacant' },
  { value: 'owner_occupied', label: 'Owner Occupied' },
  { value: 'tenanted', label: 'Tenanted' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'not_applicable', label: 'Not Applicable' },
];

// From data dictionary: tenure enum
const tenureOptions = [
  { value: 'freehold', label: 'Freehold' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'other', label: 'Other' },
  { value: 'unconfirmed', label: 'Unconfirmed' },
];

// From data dictionary: amenities enum
const amenityOptions = [
  { value: 'elevator', label: 'Elevator' },
  { value: 'balcony', label: 'Balcony' },
  { value: 'terrace', label: 'Terrace' },
  { value: 'garden', label: 'Garden' },
  { value: 'pool', label: 'Pool' },
  { value: 'storage', label: 'Storage' },
  { value: 'accessible_entry', label: 'Accessible Entry' },
  { value: 'air_conditioning', label: 'Air Conditioning' },
];

// From data dictionary: public_location_precision enum
const locationPrecisionOptions = [
  { value: 'exact', label: 'Exact' },
  { value: 'neighborhood', label: 'Neighborhood' },
  { value: 'city', label: 'City Only' },
];

const riskLevelOptions = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdminV2ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [parties, setParties] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  
  // Form state - organized by data dictionary sections
  const [formData, setFormData] = useState({
    // ===== PROJECT CORE FIELDS =====
    name: '',
    slug: '',
    strategy: 'renovate_sell',
    strategy_other: '',
    stage: 'review',
    developer_relationship: 'flippex_led',
    reporting_currency: 'EUR',
    
    // ===== PROJECT LOCATION =====
    country_code: '',
    city: '',
    
    // ===== PROJECT DATES =====
    planned_start_date: '',
    planned_exit_date: '',
    actual_start_date: '',
    actual_exit_date: '',
    
    // ===== PROJECT RELATIONSHIPS =====
    developer_id: '',
    issuer_id: '',
    
    // ===== TIMELINE =====
    duration_months: '12',
    
    // ===== CONTENT/NARRATIVE =====
    description: '',
    investment_thesis: '',
    tagline: '',
    
    // ===== RISK =====
    risk_level: 'medium',
    risk_factors: '',
    risk_mitigants: '',
    
    // ===== PAYOUT/EXIT =====
    payout_schedule: '',
    exit_options: '',
    redemption_available: false,
    redemption_terms: '',
    
    // ===== DISPLAY =====
    featured: false,
    tags: '',
    
    // ===== IMAGES =====
    image_urls: [],
    cover_image_index: 0,
    
    // ===== AUDIT =====
    reason: '',
  });

  // Asset form state
  const [assetFormData, setAssetFormData] = useState({
    // Core
    name: '',
    property_use: 'residential',
    property_form: 'apartment',
    other_description: '',
    description: '',
    
    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country_code: '',
    neighborhood: '',
    
    // Location
    latitude: '',
    longitude: '',
    public_location_precision: 'neighborhood',
    
    // Tenure
    tenure: 'freehold',
    registry_reference: '',
    cadastral_reference: '',
    
    // Property State - Current
    condition: 'good',
    occupancy: 'vacant',
    usable_area_m2: '',
    gross_area_m2: '',
    external_area_m2: '',
    plot_area_m2: '',
    bedrooms: '',
    bathrooms: '',
    floor_number: '',
    floor_count: '',
    unit_count: '',
    parking_spaces: '',
    year_built: '',
    year_renovated: '',
    energy_rating: '',
    energy_scheme: '',
    amenities: [],
    
    // Images
    image_urls: [],
    cover_image_index: 0,
  });
  
  // Sponsor form state  
  const [sponsorFormData, setSponsorFormData] = useState({
    name: '',
    description: '',
    biography: '',
    track_record: '',
    website: '',
    logo_url: '',
    team_members: [],
    specialisms: [],
    operating_regions: [],
  });

  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [showAssetPickerDialog, setShowAssetPickerDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showSponsorDialog, setShowSponsorDialog] = useState(false);
  const [showInstrumentDialog, setShowInstrumentDialog] = useState(false);
  const [showInstrumentPickerDialog, setShowInstrumentPickerDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [selectedNewStatus, setSelectedNewStatus] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [imageTarget, setImageTarget] = useState('project');
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [allAssets, setAllAssets] = useState([]);
  const [allInstruments, setAllInstruments] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [instrumentSearchQuery, setInstrumentSearchQuery] = useState('');
  
  // Financial movements state
  const [financeEntries, setFinanceEntries] = useState([]);
  const [financeReport, setFinanceReport] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [showFinanceDialog, setShowFinanceDialog] = useState(false);
  const [editingFinanceEntry, setEditingFinanceEntry] = useState(null);
  

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchWorkspace = useCallback(async () => {
    try {
      const res = await api.get(`/admin/workspace/projects/${projectId}`);
      setWorkspace(res.data);
    } catch {
      setWorkspace(null);
    }
  }, [projectId]);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const [projectRes, assetsRes, partiesRes, instrumentsRes] = await Promise.all([
        api.get(`/admin/projects/${projectId}`),
        api.get(`/admin/projects/${projectId}/assets`).catch(() => ({ data: { assets: [] } })),
        api.get('/admin/v2/parties').catch(() => ({ data: { parties: [] } })),
        api.get(`/admin/projects/${projectId}/instruments`).catch(() => ({ data: { instruments: [] } }))
      ]);
      
      const p = projectRes.data;
      setProject(p);
      setAssets(assetsRes.data.assets || []);
      setParties(partiesRes.data.parties || []);
      setInstruments(instrumentsRes.data.instruments || []);
      
      // Populate form with project data
      setFormData({
        name: p.name || p.title || '',
        slug: p.slug || '',
        strategy: p.strategy || p.project_type || 'renovate_sell',
        strategy_other: p.strategy_other || '',
        stage: p.stage || 'review',
        developer_relationship: p.developer_relationship || 'flippex_led',
        reporting_currency: p.reporting_currency || 'EUR',
        country_code: p.country_code || p.primary_location?.country_code || '',
        city: p.city || p.primary_location?.city || '',
        planned_start_date: p.planned_start_date?.split('T')[0] || p.raise_start_date?.split('T')[0] || '',
        planned_exit_date: p.planned_exit_date?.split('T')[0] || p.expected_exit_date?.split('T')[0] || '',
        actual_start_date: p.actual_start_date?.split('T')[0] || p.operations_start_date?.split('T')[0] || '',
        actual_exit_date: p.actual_exit_date?.split('T')[0] || '',
        developer_id: p.developer_id || '',
        issuer_id: p.issuer_id || '',
        duration_months: p.duration_months?.toString() || '12',
        description: p.description || '',
        investment_thesis: p.investment_thesis || '',
        tagline: p.tagline || '',
        card_thesis: p.card_thesis || '',
        risk_level: p.risk_level || 'medium',
        risk_factors: p.risk_factors || '',
        risk_mitigants: p.risk_mitigants || '',
        payout_schedule: p.payout_schedule || '',
        exit_options: p.exit_options || '',
        redemption_available: p.redemption_available || false,
        redemption_terms: p.redemption_terms || '',
        featured: p.featured || false,
        tags: p.tags?.join(', ') || '',
        image_urls: p.image_urls || [],
        cover_image_index: p.cover_image_index || 0,
        reason: '',
      });
      
      // Populate sponsor data if available
      if (p.sponsor || p.developer_profile) {
        const sponsor = p.sponsor || p.developer_profile || {};
        setSponsorFormData({
          name: sponsor.name || sponsor.display_name || '',
          description: sponsor.description || '',
          biography: sponsor.biography || '',
          track_record: sponsor.track_record || '',
          website: sponsor.website || '',
          logo_url: sponsor.logo_url || '',
          team_members: sponsor.team || sponsor.team_members || [],
          specialisms: sponsor.specialisms || [],
          operating_regions: sponsor.operating_regions || [],
        });
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
    fetchWorkspace();
  }, [fetchProject, fetchWorkspace]);

  // Fetch financial entries when tab changes to financials
  const fetchFinanceEntries = useCallback(async () => {
    if (!projectId) return;
    setFinanceLoading(true);
    try {
      const [entriesRes, reportRes] = await Promise.all([
        api.get(`/admin/v2/finance/projects/${projectId}/entries`),
        api.get(`/admin/v2/finance/projects/${projectId}/budget-vs-actual`, {
          params: { project_type: formData.strategy || undefined },
        }),
      ]);
      setFinanceEntries(entriesRes.data.entries || []);
      setFinanceReport(reportRes.data);
    } catch (err) {
      console.error('Error fetching finance entries:', err);
      // Don't show error - might just be no entries
    } finally {
      setFinanceLoading(false);
    }
  }, [projectId, formData.strategy]);

  useEffect(() => {
    if (activeTab === 'financials') {
      fetchFinanceEntries();
    }
  }, [activeTab, fetchFinanceEntries]);

  // ============================================================================
  // SAVE HANDLERS
  // ============================================================================

  const handleSave = async () => {
    if (!formData.reason.trim() || formData.reason.length < 5) {
      toast.error('Please provide a reason for changes (min 5 characters)');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: formData.name || null,
        name: formData.name || null,
        slug: formData.slug || null,
        tagline: formData.tagline || null,
        card_thesis: formData.card_thesis || null,
        description: formData.description || null,
        investment_thesis: formData.investment_thesis || null,
        project_type: formData.strategy || null,
        strategy: formData.strategy || null,
        strategy_other: formData.strategy_other || null,
        developer_relationship: formData.developer_relationship || null,
        developer_id: formData.developer_id || null,
        issuer_id: formData.issuer_id || null,
        reporting_currency: formData.reporting_currency || null,
        country_code: formData.country_code || null,
        city: formData.city || null,
        duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
        risk_level: formData.risk_level || null,
        risk_factors: formData.risk_factors || null,
        risk_mitigants: formData.risk_mitigants || null,
        payout_schedule: formData.payout_schedule || null,
        exit_options: formData.exit_options || null,
        redemption_available: formData.redemption_available,
        redemption_terms: formData.redemption_terms || null,
        featured: formData.featured,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : null,
        image_urls: formData.image_urls,
        cover_image_index: formData.cover_image_index,
        planned_start_date: formData.planned_start_date || null,
        planned_exit_date: formData.planned_exit_date || null,
        actual_start_date: formData.actual_start_date || null,
        actual_exit_date: formData.actual_exit_date || null,
        reason: formData.reason,
      };
      
      await api.put(`/admin/projects/${projectId}`, payload);
      toast.success('Project updated successfully');
      setEditMode(false);
      setFormData(prev => ({ ...prev, reason: '' }));
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // IMAGE HANDLERS
  // ============================================================================

  const handleAddImage = () => {
    if (!newImageUrl.trim() || !newImageUrl.startsWith('http')) {
      toast.error('Please enter a valid image URL');
      return;
    }
    
    if (imageTarget === 'project') {
      setFormData(prev => ({
        ...prev,
        image_urls: [...prev.image_urls, newImageUrl]
      }));
    } else if (imageTarget === 'asset') {
      setAssetFormData(prev => ({
        ...prev,
        image_urls: [...prev.image_urls, newImageUrl]
      }));
    }
    
    setNewImageUrl('');
    setShowImageDialog(false);
    toast.success('Image added - save changes to persist');
  };

  const handleRemoveImage = (index, target = 'project') => {
    if (target === 'project') {
      setFormData(prev => ({
        ...prev,
        image_urls: prev.image_urls.filter((_, i) => i !== index),
        cover_image_index: prev.cover_image_index === index ? 0 : 
          (prev.cover_image_index > index ? prev.cover_image_index - 1 : prev.cover_image_index)
      }));
    } else {
      setAssetFormData(prev => ({
        ...prev,
        image_urls: prev.image_urls.filter((_, i) => i !== index),
        cover_image_index: prev.cover_image_index === index ? 0 : 
          (prev.cover_image_index > index ? prev.cover_image_index - 1 : prev.cover_image_index)
      }));
    }
    toast.success('Image removed - save changes to persist');
  };

  const handleSetCover = (index, target = 'project') => {
    if (target === 'project') {
      setFormData(prev => ({ ...prev, cover_image_index: index }));
    } else {
      setAssetFormData(prev => ({ ...prev, cover_image_index: index }));
    }
  };

  const moveImage = (index, direction, target = 'project') => {
    const setter = target === 'project' ? setFormData : setAssetFormData;
    setter(prev => {
      const newUrls = [...prev.image_urls];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= newUrls.length) return prev;
      [newUrls[index], newUrls[newIndex]] = [newUrls[newIndex], newUrls[index]];
      
      let newCoverIndex = prev.cover_image_index;
      if (prev.cover_image_index === index) newCoverIndex = newIndex;
      else if (prev.cover_image_index === newIndex) newCoverIndex = index;
      
      return { ...prev, image_urls: newUrls, cover_image_index: newCoverIndex };
    });
  };

  // ============================================================================
  // ASSET HANDLERS
  // ============================================================================

  const handleOpenAssetDialog = (asset = null) => {
    if (asset) {
      setEditingAssetId(asset.id);
      const chars = asset.characteristics || {};
      const loc = asset.location || {};
      setAssetFormData({
        name: asset.name || '',
        property_use: asset.property_use || 'residential',
        property_form: asset.property_form || asset.asset_type || 'apartment',
        other_description: asset.other_description || '',
        description: asset.description || chars.description || '',
        address_line1: loc.address_line_1 || '',
        address_line2: loc.address_line_2 || '',
        city: loc.city || '',
        region: loc.region || '',
        postal_code: loc.postal_code || '',
        country_code: loc.country_code || '',
        neighborhood: loc.neighborhood || '',
        latitude: loc.latitude?.toString() || '',
        longitude: loc.longitude?.toString() || '',
        public_location_precision: asset.public_location_precision || 'neighborhood',
        tenure: asset.tenure || 'freehold',
        registry_reference: asset.registry_reference || '',
        cadastral_reference: asset.cadastral_reference || '',
        condition: chars.condition || 'good',
        occupancy: chars.occupancy || 'vacant',
        usable_area_m2: chars.usable_area_m2?.toString() || chars.total_area_sqm?.toString() || '',
        gross_area_m2: chars.gross_area_m2?.toString() || '',
        external_area_m2: chars.external_area_m2?.toString() || '',
        plot_area_m2: chars.plot_area_m2?.toString() || '',
        bedrooms: chars.bedrooms?.toString() || '',
        bathrooms: chars.bathrooms?.toString() || '',
        floor_number: chars.floor_number?.toString() || '',
        floor_count: chars.floor_count?.toString() || chars.floors_count?.toString() || '',
        unit_count: chars.unit_count?.toString() || chars.units_count?.toString() || '',
        parking_spaces: chars.parking_spaces?.toString() || '',
        year_built: chars.year_built?.toString() || '',
        year_renovated: chars.year_renovated?.toString() || '',
        energy_rating: chars.energy_rating || '',
        energy_scheme: chars.energy_scheme || '',
        amenities: chars.amenities || [],
        image_urls: asset.image_urls || [],
        cover_image_index: asset.cover_image_index || 0,
      });
    } else {
      setEditingAssetId(null);
      setAssetFormData({
        name: '',
        property_use: 'residential',
        property_form: 'apartment',
        other_description: '',
        description: '',
        address_line1: '',
        address_line2: '',
        city: formData.city || '',
        region: '',
        postal_code: '',
        country_code: formData.country_code || '',
        neighborhood: '',
        latitude: '',
        longitude: '',
        public_location_precision: 'neighborhood',
        tenure: 'freehold',
        registry_reference: '',
        cadastral_reference: '',
        condition: 'good',
        occupancy: 'vacant',
        usable_area_m2: '',
        gross_area_m2: '',
        external_area_m2: '',
        plot_area_m2: '',
        bedrooms: '',
        bathrooms: '',
        floor_number: '',
        floor_count: '',
        unit_count: '',
        parking_spaces: '',
        year_built: '',
        year_renovated: '',
        energy_rating: '',
        energy_scheme: '',
        amenities: [],
        image_urls: [],
        cover_image_index: 0,
      });
    }
    setShowAssetDialog(true);
  };

  const handleSaveAsset = async () => {
    if (!assetFormData.city || !assetFormData.country_code) {
      toast.error('City and country code are required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: assetFormData.name || `${assetFormData.property_form} in ${assetFormData.city}`,
        asset_type: assetFormData.property_form,
        property_use: assetFormData.property_use,
        property_form: assetFormData.property_form,
        other_description: assetFormData.other_description || null,
        description: assetFormData.description || null,
        city: assetFormData.city,
        country_code: assetFormData.country_code,
        address_line_1: assetFormData.address_line1 || null,
        address_line_2: assetFormData.address_line2 || null,
        region: assetFormData.region || null,
        postal_code: assetFormData.postal_code || null,
        neighborhood: assetFormData.neighborhood || null,
        latitude: assetFormData.latitude ? parseFloat(assetFormData.latitude) : null,
        longitude: assetFormData.longitude ? parseFloat(assetFormData.longitude) : null,
        public_location_precision: assetFormData.public_location_precision,
        tenure: assetFormData.tenure,
        registry_reference: assetFormData.registry_reference || null,
        cadastral_reference: assetFormData.cadastral_reference || null,
        characteristics: {
          condition: assetFormData.condition,
          occupancy: assetFormData.occupancy,
          usable_area_m2: assetFormData.usable_area_m2 ? parseFloat(assetFormData.usable_area_m2) : null,
          total_area_sqm: assetFormData.usable_area_m2 ? parseFloat(assetFormData.usable_area_m2) : null,
          gross_area_m2: assetFormData.gross_area_m2 ? parseFloat(assetFormData.gross_area_m2) : null,
          external_area_m2: assetFormData.external_area_m2 ? parseFloat(assetFormData.external_area_m2) : null,
          plot_area_m2: assetFormData.plot_area_m2 ? parseFloat(assetFormData.plot_area_m2) : null,
          bedrooms: assetFormData.bedrooms ? parseInt(assetFormData.bedrooms) : null,
          bathrooms: assetFormData.bathrooms ? parseFloat(assetFormData.bathrooms) : null,
          floor_number: assetFormData.floor_number ? parseInt(assetFormData.floor_number) : null,
          floor_count: assetFormData.floor_count ? parseInt(assetFormData.floor_count) : null,
          floors_count: assetFormData.floor_count ? parseInt(assetFormData.floor_count) : null,
          unit_count: assetFormData.unit_count ? parseInt(assetFormData.unit_count) : null,
          units_count: assetFormData.unit_count ? parseInt(assetFormData.unit_count) : null,
          parking_spaces: assetFormData.parking_spaces ? parseInt(assetFormData.parking_spaces) : null,
          year_built: assetFormData.year_built ? parseInt(assetFormData.year_built) : null,
          year_renovated: assetFormData.year_renovated ? parseInt(assetFormData.year_renovated) : null,
          energy_rating: assetFormData.energy_rating || null,
          energy_scheme: assetFormData.energy_scheme || null,
          amenities: assetFormData.amenities,
          description: assetFormData.description || null,
        },
        image_urls: assetFormData.image_urls,
        cover_image_index: assetFormData.cover_image_index,
      };
      
      if (editingAssetId) {
        await api.put(`/admin/v2/projects/assets/${editingAssetId}`, payload);
        toast.success('Asset updated successfully');
      } else {
        await api.post(`/admin/projects/${projectId}/assets`, payload);
        toast.success('Asset created and linked');
      }
      
      setShowAssetDialog(false);
      setEditingAssetId(null);
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const toggleAmenity = (amenity) => {
    setAssetFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  // ============================================================================
  // WORKFLOW HANDLERS
  // ============================================================================

  const handleWorkflowAction = async (action) => {
    setSaving(true);
    try {
      let endpoint = '';
      switch (action) {
        case 'submit':
          endpoint = `/admin/projects/${projectId}/submit-for-review`;
          break;
        case 'approve':
          endpoint = `/admin/projects/${projectId}/approve`;
          break;
        case 'reject':
          endpoint = `/admin/projects/${projectId}/reject`;
          break;
        case 'publish':
          endpoint = `/admin/projects/${projectId}/publish`;
          break;
        default:
          return;
      }
      
      const payload = action === 'submit' ? {} : { reason: `Admin ${action} action` };
      await api.post(endpoint, payload);
      toast.success(`Project ${action} successful`);
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteReason.trim() || deleteReason.length < 5) {
      toast.error('Please provide a reason for deletion (min 5 characters)');
      return;
    }
    
    setSaving(true);
    try {
      await api.delete(`/admin/v2/projects/${projectId}`, {
        data: { reason: deleteReason }
      });
      toast.success('Project deleted successfully');
      navigate('/admin/v2/projects');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete project');
    } finally {
      setSaving(false);
      setShowDeleteDialog(false);
      setDeleteReason('');
    }
  };

  const handleStatusChange = async () => {
    if (!statusChangeReason.trim() || statusChangeReason.length < 5) {
      toast.error('Please provide a reason for the status change (min 5 characters)');
      return;
    }
    if (!selectedNewStatus) {
      toast.error('Please select a new status');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/admin/v2/projects/${projectId}/status`, {
        new_status: selectedNewStatus,
        reason: statusChangeReason
      });
      toast.success(`Status changed to ${selectedNewStatus}`);
      setShowStatusDialog(false);
      setStatusChangeReason('');
      setSelectedNewStatus('');
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change status');
    } finally {
      setSaving(false);
    }
  };

  // Get available status transitions based on current status
  const getAvailableStatusTransitions = () => {
    const transitions = {
      draft: ['pending_review', 'cancelled'],
      pending_review: ['approved', 'draft'],
      approved: ['published', 'draft'],
      published: ['funding'],
      funding: ['funded', 'cancelled'],
      funded: ['in_execution'],
      in_execution: ['operating', 'exiting'],
      operating: ['exiting'],
      exiting: ['exited'],
      exited: ['archived'],
      cancelled: ['archived'],
      archived: [],
    };
    return transitions[project?.status] || [];
  };

  // ============================================================================
  // ASSET PICKER HANDLERS
  // ============================================================================

  const fetchAllAssets = async () => {
    try {
      const res = await api.get('/admin/projects/assets/all');
      setAllAssets(res.data.assets || []);
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    }
  };

  const handleOpenAssetPicker = () => {
    fetchAllAssets();
    setAssetSearchQuery('');
    setShowAssetPickerDialog(true);
  };

  const handleLinkAsset = async (assetId) => {
    setSaving(true);
    try {
      await api.post(`/admin/projects/${projectId}/link-asset`, { asset_id: assetId });
      toast.success('Asset linked to project');
      setShowAssetPickerDialog(false);
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link asset');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to unlink this asset from the project?')) {
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/admin/projects/${projectId}/unlink-asset`, { asset_id: assetId });
      toast.success('Asset unlinked from project');
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink asset');
    } finally {
      setSaving(false);
    }
  };

  // Filter assets for picker (exclude already linked)
  const linkedAssetIds = assets.map(a => a.id);
  const availableAssets = allAssets.filter(a => !linkedAssetIds.includes(a.id));
  const filteredAssets = availableAssets.filter(a => {
    if (!assetSearchQuery) return true;
    const query = assetSearchQuery.toLowerCase();
    return (
      (a.name && a.name.toLowerCase().includes(query)) ||
      (a.code && a.code.toLowerCase().includes(query)) ||
      (a.city && a.city.toLowerCase().includes(query)) ||
      (a.location && a.location.toLowerCase().includes(query))
    );
  });

  // ============================================================================
  // INSTRUMENT HANDLERS
  // ============================================================================

  const fetchAllInstruments = async () => {
    try {
      const res = await api.get('/admin/projects/instruments/all');
      setAllInstruments(res.data.instruments || []);
    } catch (err) {
      console.error('Error fetching instruments:', err);
    }
  };

  const handleOpenInstrumentDialog = (instrument = null) => {
    setEditingInstrument(instrument);
    setShowInstrumentDialog(true);
  };

  const handleOpenInstrumentPicker = () => {
    fetchAllInstruments();
    setInstrumentSearchQuery('');
    setShowInstrumentPickerDialog(true);
  };

  const handleLinkInstrument = async (instrumentId) => {
    setSaving(true);
    try {
      await api.post(`/admin/projects/${projectId}/link-instrument`, { instrument_id: instrumentId });
      toast.success('Instrument linked to project');
      setShowInstrumentPickerDialog(false);
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link instrument');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkInstrument = async (instrumentId) => {
    if (!window.confirm('Are you sure you want to unlink this instrument from the project?')) {
      return;
    }

    setSaving(true);
    try {
      await api.post(`/admin/projects/${projectId}/unlink-instrument`, { instrument_id: instrumentId });
      toast.success('Instrument unlinked from project');
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink instrument');
    } finally {
      setSaving(false);
    }
  };

  // Filter instruments for picker (exclude already linked)
  const linkedInstrumentIds = instruments.map(i => i.id);
  const availableInstruments = allInstruments.filter(i => !linkedInstrumentIds.includes(i.id) && !i.project_id);
  const filteredInstruments = availableInstruments.filter(i => {
    if (!instrumentSearchQuery) return true;
    const query = instrumentSearchQuery.toLowerCase();
    return (
      (i.name && i.name.toLowerCase().includes(query)) ||
      (i.code && i.code.toLowerCase().includes(query))
    );
  });

  // ============================================================================
  // FINANCE ENTRY HANDLERS
  // ============================================================================

  const handleOpenFinanceDialog = (entry = null) => {
    setEditingFinanceEntry(entry);
    setShowFinanceDialog(true);
  };

  const handleDeleteFinanceEntry = async (entryId) => {
    // Note: The current V2 finance API doesn't have a delete endpoint
    // This would need to be implemented in the backend
    toast.error('Delete not yet implemented - use reversal workflow instead');
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <h2 className="text-xl font-semibold mb-2">Project Not Found</h2>
            <Button onClick={() => navigate('/admin/v2/projects')} data-testid="back-to-projects-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Projects
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const status = statusConfig[project.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  
  // Calculate totals from linked instruments
  const instrumentsSummary = instruments.reduce((acc, inst) => ({
    totalAuthorized: acc.totalAuthorized + (inst.total_authorized_units || inst.authorized_units || 0),
    totalOutstanding: acc.totalOutstanding + (inst.units_outstanding || inst.outstanding_units || 0),
    totalValue: acc.totalValue + ((inst.units_outstanding || inst.outstanding_units || 0) * parseFloat(inst.unit_price_at_issuance || inst.price_per_unit || 1))
  }), { totalAuthorized: 0, totalOutstanding: 0, totalValue: 0 });
  
  const isEditable = true; // Admins can edit all projects regardless of status

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-background" data-testid="admin-v2-project-detail-page">
      
      <main className="max-w-[1200px]">
        <ProjectWorkspaceHeader
          project={{ ...project, title: formData.name || project.title, status: project.status, featured: formData.featured }}
          overview={workspace}
          tabs={PROJECT_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          editMode={editMode}
          isEditable={isEditable}
          saving={saving}
          onEdit={() => setEditMode(true)}
          onCancelEdit={() => { setEditMode(false); fetchProject(); }}
          onSave={handleSave}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab}>

          {/* ================================================================ */}
          {/* OVERVIEW TAB */}
          {/* ================================================================ */}
          <TabsContent value="overview" className="space-y-5">
            <ProjectOverviewSummary
              projectId={project.id}
              overview={workspace}
              onNavigateTab={setActiveTab}
              onRefresh={fetchWorkspace}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Description */}
              <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Description
                </h3>
                {editMode ? (
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    placeholder="Detailed project description..."
                    data-testid="description-input"
                  />
                ) : (
                  <p className="text-foreground-muted whitespace-pre-wrap">
                    {formData.description || 'No description provided'}
                  </p>
                )}
              </Card>

              {/* Investment Thesis */}
              <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Investment Thesis
                </h3>
                {editMode ? (
                  <Textarea
                    value={formData.investment_thesis}
                    onChange={(e) => setFormData({ ...formData, investment_thesis: e.target.value })}
                    rows={6}
                    placeholder="Why invest in this project..."
                    data-testid="thesis-input"
                  />
                ) : (
                  <p className="text-foreground-muted whitespace-pre-wrap">
                    {formData.investment_thesis || 'No investment thesis provided'}
                  </p>
                )}
              </Card>

              {/* Risk Assessment */}
              <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Risk Assessment
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-foreground-muted">Risk Level</Label>
                    {editMode ? (
                      <Select value={formData.risk_level} onValueChange={(v) => setFormData({ ...formData, risk_level: v })}>
                        <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {riskLevelOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={cn('mt-1',
                        formData.risk_level === 'low' ? 'bg-green-100 text-green-700' :
                        formData.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      )}>
                        {formData.risk_level || 'Medium'}
                      </Badge>
                    )}
                  </div>
                  {(formData.risk_factors || editMode) && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Risk Factors</Label>
                      {editMode ? (
                        <Textarea value={formData.risk_factors} onChange={(e) => setFormData({ ...formData, risk_factors: e.target.value })} rows={3} placeholder="Key risk factors..." className="mt-1 h-9 text-sm" />
                      ) : (
                        <p className="text-sm text-foreground-muted mt-1 whitespace-pre-wrap">{formData.risk_factors}</p>
                      )}
                    </div>
                  )}
                  {(formData.risk_mitigants || editMode) && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Risk Mitigants</Label>
                      {editMode ? (
                        <Textarea value={formData.risk_mitigants} onChange={(e) => setFormData({ ...formData, risk_mitigants: e.target.value })} rows={3} placeholder="How risks are mitigated..." className="mt-1 h-9 text-sm" />
                      ) : (
                        <p className="text-sm text-foreground-muted mt-1 whitespace-pre-wrap">{formData.risk_mitigants}</p>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>

          </TabsContent>

          {/* ================================================================ */}
          {/* PROJECT DETAILS TAB */}
          {/* ================================================================ */}
          <TabsContent value="details" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Info */}
              <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
                <h3 className="font-semibold mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-foreground-muted">Project Name *</Label>
                    {editMode ? (
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1 h-9 text-sm" data-testid="name-input" />
                    ) : (
                      <p className="text-sm font-medium mt-1">{formData.name}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Tagline</Label>
                    {editMode ? (
                      <Input value={formData.tagline} onChange={(e) => setFormData({ ...formData, tagline: e.target.value })} placeholder="Brief compelling description" className="mt-1 h-9 text-sm" />
                    ) : (
                      <p className="text-foreground-muted">{formData.tagline || '—'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">URL Slug</Label>
                    {editMode ? (
                      <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="project-url-slug" className="mt-1 h-9 text-sm" />
                    ) : (
                      <p className="font-mono text-sm">{formData.slug || '—'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Strategy</Label>
                    {editMode ? (
                      <Select value={formData.strategy} onValueChange={(v) => setFormData({ ...formData, strategy: v })}>
                        <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {strategyOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-medium capitalize mt-1">{strategyOptions.find(s => s.value === formData.strategy)?.label || formData.strategy}</p>
                    )}
                  </div>
                  {formData.strategy === 'other' && editMode && (
                    <div>
                      <Label className="text-xs text-foreground-muted">Strategy Description</Label>
                      <Input value={formData.strategy_other} onChange={(e) => setFormData({ ...formData, strategy_other: e.target.value })} placeholder="Describe the strategy..." className="mt-1 h-9 text-sm" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-foreground-muted">Stage</Label>
                    <p className="font-medium capitalize" data-testid="project-stage-value">
                      {stageOptions.find(s => s.value === project.stage)?.label || (project.stage || '—')}
                    </p>
                    <p className="text-xs text-foreground-muted mt-0.5">
                      Derived from the workflow status ({statusConfig[project.status]?.label || project.status})
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Developer Relationship</Label>
                    {editMode ? (
                      <Select value={formData.developer_relationship} onValueChange={(v) => setFormData({ ...formData, developer_relationship: v })}>
                        <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {developerRelationshipOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm font-medium mt-1">{developerRelationshipOptions.find(d => d.value === formData.developer_relationship)?.label || formData.developer_relationship}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-foreground-muted">Featured</Label>
                      {editMode ? (
                        <Switch checked={formData.featured} onCheckedChange={(v) => setFormData({ ...formData, featured: v })} />
                      ) : (
                        <Badge variant={formData.featured ? 'default' : 'outline'}>{formData.featured ? 'Yes' : 'No'}</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Tags</Label>
                    {editMode ? (
                      <Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} placeholder="lisbon, residential, renovation (comma separated)" className="mt-1 h-9 text-sm" />
                    ) : (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {formData.tags ? formData.tags.split(',').map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{tag.trim()}</Badge>
                        )) : <span className="text-foreground-muted">No tags</span>}
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Location */}
              <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Primary Location
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-foreground-muted">City *</Label>
                      {editMode ? (
                        <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Lisbon" className="mt-1 h-9 text-sm" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{formData.city || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-foreground-muted">Country Code *</Label>
                      {editMode ? (
                        <Input value={formData.country_code} onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })} placeholder="PT" maxLength={2} className="mt-1 h-9 text-sm" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{formData.country_code || '—'}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Reporting Currency</Label>
                    {editMode ? (
                      <Input value={formData.reporting_currency} onChange={(e) => setFormData({ ...formData, reporting_currency: e.target.value.toUpperCase() })} placeholder="EUR" maxLength={3} className="mt-1 h-9 text-sm" />
                    ) : (
                      <p className="text-sm font-medium mt-1">{formData.reporting_currency || 'EUR'}</p>
                    )}
                  </div>
                </div>
              </Card>

              {/* Party Relationships */}
              <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Party Relationships
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-foreground-muted">Developer/Sponsor</Label>
                    {editMode ? (
                      <Select value={formData.developer_id || '_none_'} onValueChange={(v) => setFormData({ ...formData, developer_id: v === '_none_' ? '' : v })}>
                        <SelectTrigger className="mt-1 h-9 text-sm">
                          <SelectValue placeholder="Select developer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">— None —</SelectItem>
                          {developerParties(parties).map(party => (
                            <SelectItem key={party.id} value={party.id}>
                              {party.display_name} ({party.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium text-sm">
                        {formData.developer_id 
                          ? parties.find(p => p.id === formData.developer_id)?.display_name || formData.developer_id
                          : '—'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Issuer</Label>
                    {editMode ? (
                      <Select value={formData.issuer_id || '_none_'} onValueChange={(v) => setFormData({ ...formData, issuer_id: v === '_none_' ? '' : v })}>
                        <SelectTrigger className="mt-1 h-9 text-sm">
                          <SelectValue placeholder="Select issuer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">— None —</SelectItem>
                          {issuerParties(parties).map(party => (
                            <SelectItem key={party.id} value={party.id}>
                              {party.display_name} ({party.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium text-sm">
                        {formData.issuer_id 
                          ? parties.find(p => p.id === formData.issuer_id)?.display_name || formData.issuer_id
                          : '—'}
                      </p>
                    )}
                  </div>
                </div>
              </Card>

              {/* Timeline */}
              <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Timeline
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-foreground-muted">Planned Start Date</Label>
                    {editMode ? (
                      <Input type="date" value={formData.planned_start_date} onChange={(e) => setFormData({ ...formData, planned_start_date: e.target.value })} className="mt-1 h-9 text-sm" />
                    ) : (
                      <p className="text-sm font-medium mt-1">{formData.planned_start_date ? new Date(formData.planned_start_date).toLocaleDateString() : '—'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Planned Exit Date</Label>
                    {editMode ? (
                      <Input type="date" value={formData.planned_exit_date} onChange={(e) => setFormData({ ...formData, planned_exit_date: e.target.value })} className="mt-1 h-9 text-sm" />
                    ) : (
                      <p className="text-sm font-medium mt-1">{formData.planned_exit_date ? new Date(formData.planned_exit_date).toLocaleDateString() : '—'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Actual Start Date</Label>
                    {editMode ? (
                      <Input type="date" value={formData.actual_start_date} onChange={(e) => setFormData({ ...formData, actual_start_date: e.target.value })} className="mt-1 h-9 text-sm" />
                    ) : (
                      <p className="text-sm font-medium mt-1">{formData.actual_start_date ? new Date(formData.actual_start_date).toLocaleDateString() : '—'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Actual Exit Date</Label>
                    {editMode ? (
                      <Input type="date" value={formData.actual_exit_date} onChange={(e) => setFormData({ ...formData, actual_exit_date: e.target.value })} className="mt-1 h-9 text-sm" />
                    ) : (
                      <p className="text-sm font-medium mt-1">{formData.actual_exit_date ? new Date(formData.actual_exit_date).toLocaleDateString() : '—'}</p>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* ================================================================ */}
          {/* ASSETS TAB */}
          {/* ================================================================ */}
          <TabsContent value="assets" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Linked Assets</h3>
                <p className="text-foreground-muted text-sm">Physical properties and buildings associated with this project</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleOpenAssetPicker} data-testid="link-existing-asset-btn">
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Link Existing Asset
                </Button>
                {isEditable && (
                  <Button onClick={() => handleOpenAssetDialog()} data-testid="add-asset-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Asset
                  </Button>
                )}
              </div>
            </div>

            {assets.length === 0 ? (
              <Card className="bg-white p-12 text-center rounded-xl border border-black/[0.04]">
                <Building2 className="h-12 w-12 text-foreground-muted mx-auto mb-4" />
                <h4 className="font-medium mb-2">No Assets Linked</h4>
                <p className="text-foreground-muted text-sm mb-4">Add physical properties or buildings to this project</p>
                {isEditable && (
                  <Button variant="outline" onClick={() => handleOpenAssetDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Asset
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {assets.map((asset) => {
                  const chars = asset.characteristics || {};
                  const loc = asset.location || {};
                  return (
                    <Card key={asset.id} className="bg-white p-5 rounded-xl border border-black/[0.04] hover:border-primary/50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="cursor-pointer" onClick={() => handleOpenAssetDialog(asset)}>
                          <span className="font-mono text-xs text-foreground-muted">{asset.code}</span>
                          <h4 className="font-medium mt-1">{asset.name || `${asset.asset_type?.replace('_', ' ')} in ${loc.city || '—'}`}</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">{asset.property_use || asset.asset_type?.replace('_', ' ')}</Badge>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-7 w-7 text-foreground-muted hover:text-red-600 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleUnlinkAsset(asset.id); }}
                            data-testid={`unlink-asset-${asset.id}`}
                            title="Unlink asset from project"
                          >
                            <Unlink className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="text-sm text-foreground-muted mb-3">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {loc.city}, {loc.country_code}
                        {loc.neighborhood && ` - ${loc.neighborhood}`}
                      </div>

                      <div className="grid grid-cols-4 gap-2 text-sm p-3 bg-slate-50 rounded-lg">
                        {chars.bedrooms != null && (
                          <div className="flex items-center gap-1">
                            <Bed className="h-3 w-3 text-foreground-muted" />
                            <span>{chars.bedrooms} beds</span>
                          </div>
                        )}
                        {chars.bathrooms != null && (
                          <div className="flex items-center gap-1">
                            <Bath className="h-3 w-3 text-foreground-muted" />
                            <span>{chars.bathrooms} baths</span>
                          </div>
                        )}
                        {(chars.usable_area_m2 || chars.total_area_sqm) && (
                          <div className="flex items-center gap-1">
                            <Ruler className="h-3 w-3 text-foreground-muted" />
                            <span>{chars.usable_area_m2 || chars.total_area_sqm} m²</span>
                          </div>
                        )}
                        {(chars.floor_count || chars.floors_count) && (
                          <div className="flex items-center gap-1">
                            <Layers className="h-3 w-3 text-foreground-muted" />
                            <span>{chars.floor_count || chars.floors_count} floors</span>
                          </div>
                        )}
                        {chars.year_built && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-foreground-muted" />
                            <span>Built {chars.year_built}</span>
                          </div>
                        )}
                        {chars.parking_spaces != null && (
                          <div className="flex items-center gap-1">
                            <Car className="h-3 w-3 text-foreground-muted" />
                            <span>{chars.parking_spaces} parking</span>
                          </div>
                        )}
                      </div>

                      {asset.current_valuation && (
                        <div className="mt-3 pt-3 border-t border-black/[0.04] flex justify-between items-center">
                          <span className="text-xs text-foreground-muted">Valuation</span>
                          <p className="font-semibold">€{parseInt(asset.current_valuation).toLocaleString()}</p>
                        </div>
                      )}
                      
                      {asset.image_urls?.length > 0 && (
                        <div className="mt-3 flex gap-1">
                          {asset.image_urls.slice(0, 4).map((url, i) => (
                            <img key={i} src={url} alt="" className="h-12 w-12 rounded object-cover" />
                          ))}
                          {asset.image_urls.length > 4 && (
                            <div className="h-12 w-12 rounded bg-slate-100 flex items-center justify-center text-xs text-foreground-muted">
                              +{asset.image_urls.length - 4}
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ================================================================ */}
          {/* INSTRUMENTS TAB */}
          {/* ================================================================ */}
          <TabsContent value="instruments" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Financial Instruments</h3>
                <p className="text-foreground-muted text-sm">Securities and investment units issued for this project</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleOpenInstrumentPicker} data-testid="link-existing-instrument-btn">
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Link Existing Instrument
                </Button>
                {isEditable && (
                  <Button onClick={() => handleOpenInstrumentDialog()} data-testid="add-instrument-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Instrument
                  </Button>
                )}
              </div>
            </div>

            {instruments.length === 0 ? (
              <Card className="bg-slate-50 border-dashed p-12 text-center">
                <Briefcase className="h-12 w-12 text-foreground-muted mx-auto mb-4" />
                <h4 className="font-medium mb-2">No Instruments Linked</h4>
                <p className="text-foreground-muted text-sm mb-4">Create or link financial instruments (securities) to this project.</p>
                <div className="flex items-center justify-center gap-2">
                  <Button variant="outline" onClick={handleOpenInstrumentPicker} data-testid="link-instrument-empty-btn">
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Link Existing
                  </Button>
                  <Button onClick={() => handleOpenInstrumentDialog()} data-testid="create-instrument-empty-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {instruments.map((instrument) => (
                  <InstrumentCard
                    key={instrument.id}
                    instrument={instrument}
                    onEdit={handleOpenInstrumentDialog}
                    onRefresh={fetchProject}
                    onUnlink={handleUnlinkInstrument}
                  />
                ))}
              </div>
            )}

            {/* Instruments Info Card */}
            <Card className="bg-blue-50/50 border-blue-100 p-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">About Financial Instruments</p>
                  <p className="text-blue-700 mt-1">
                    Instruments represent the securities or units that investors purchase in this project. 
                    Each instrument defines the total authorized units, pricing, and investor rights 
                    (distributions, voting, transferability). Once units are issued to investors, 
                    they are tracked in the Cap Table.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ================================================================ */}
          {/* FINANCIALS TAB */}
          {/* ================================================================ */}
          <TabsContent value="financials" className="space-y-6">
            {/* Project financials workspace: plan, record, review, approve, analyse */}
            <ProjectFinancialsWorkspace
              projectId={projectId}
              projectType={formData.strategy}
            />


            {/* NAV Management Section */}
            <NavManagement
              projectId={project?.legacy_id || project?.id}
              v2ProjectId={projectId}
            />
          </TabsContent>

          {/* ================================================================ */}
          {/* SPONSOR TAB */}
          {/* ================================================================ */}
          <TabsContent value="sponsor" className="space-y-6">
            <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Developer / Sponsor
                </h3>
              </div>
              
              <div className="space-y-6">
                {/* Developer Selection */}
                <div className="space-y-2">
                  <Label className="text-xs text-foreground-muted">Developer Party</Label>
                  <p className="text-xs text-foreground-muted mb-2">
                    Select the developer/sponsor responsible for this project from existing parties.
                  </p>
                  {editMode ? (
                    <div className="space-y-2">
                      <Select
                        value={formData.developer_id || '_none_'}
                        onValueChange={(value) => setFormData({ ...formData, developer_id: value === '_none_' ? null : value })}
                      >
                        <SelectTrigger data-testid="developer-select">
                          <SelectValue placeholder="Select a developer..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">— No developer selected —</SelectItem>
                          {developerParties(parties).map(party => (
                            <SelectItem key={party.id} value={party.id}>
                              {party.display_name} ({party.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {developerParties(parties).length === 0 && (
                        <p className="text-sm text-amber-600">
                          No developer parties exist. Create one in Party Management first.
                        </p>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open('/admin/parties?create=developer', '_blank')}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Developer Party
                      </Button>
                    </div>
                  ) : (
                    <div>
                      {formData.developer_id ? (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium mt-1">
                              {parties.find(p => p.id === formData.developer_id)?.display_name || 'Unknown Party'}
                            </p>
                            <p className="text-xs text-foreground-muted">
                              {parties.find(p => p.id === formData.developer_id)?.code || formData.developer_id}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={() => window.open(`/admin/parties/${formData.developer_id}`, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-foreground-muted">No developer assigned</p>
                      )}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Issuer Selection */}
                <div className="space-y-2">
                  <Label className="text-xs text-foreground-muted">Issuer Party</Label>
                  <p className="text-xs text-foreground-muted mb-2">
                    Select the legal entity that will issue securities for this project.
                  </p>
                  {editMode ? (
                    <div className="space-y-2">
                      <Select
                        value={formData.issuer_id || '_none_'}
                        onValueChange={(value) => setFormData({ ...formData, issuer_id: value === '_none_' ? null : value })}
                      >
                        <SelectTrigger data-testid="issuer-select">
                          <SelectValue placeholder="Select an issuer..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">— No issuer selected —</SelectItem>
                          {issuerParties(parties).map(party => (
                            <SelectItem key={party.id} value={party.id}>
                              {party.display_name} ({party.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {issuerParties(parties).length === 0 && (
                        <p className="text-sm text-amber-600">
                          No issuer parties exist. Create one in Party Management first.
                        </p>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open('/admin/parties?create=issuer', '_blank')}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Issuer Party
                      </Button>
                    </div>
                  ) : (
                    <div>
                      {formData.issuer_id ? (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Landmark className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium mt-1">
                              {parties.find(p => p.id === formData.issuer_id)?.display_name || 'Unknown Party'}
                            </p>
                            <p className="text-xs text-foreground-muted">
                              {parties.find(p => p.id === formData.issuer_id)?.code || formData.issuer_id}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto"
                            onClick={() => window.open(`/admin/parties/${formData.issuer_id}`, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-foreground-muted">No issuer assigned</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ================================================================ */}
          {/* IMAGES TAB */}
          {/* ================================================================ */}
          <TabsContent value="images" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Project Images</h3>
                <p className="text-foreground-muted text-sm">Manage project gallery images. Drag to reorder.</p>
              </div>
              {isEditable && (
                <Button onClick={() => { setImageTarget('project'); setShowImageDialog(true); }} data-testid="add-image-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Image
                </Button>
              )}
            </div>

            {formData.image_urls.length === 0 ? (
              <Card className="bg-white p-12 text-center rounded-xl border border-black/[0.04]">
                <ImageIcon className="h-12 w-12 text-foreground-muted mx-auto mb-4" />
                <h4 className="font-medium mb-2">No Images</h4>
                <p className="text-foreground-muted text-sm mb-4">Add images to showcase this project</p>
                {isEditable && (
                  <Button variant="outline" onClick={() => { setImageTarget('project'); setShowImageDialog(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Image
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.image_urls.map((url, index) => (
                  <Card 
                    key={index} 
                    className={cn(
                      "relative group overflow-hidden rounded-xl border-2",
                      formData.cover_image_index === index ? "border-primary" : "border-transparent hover:border-primary/50"
                    )}
                  >
                    <img src={url} alt={`Project ${index + 1}`} className="w-full h-40 object-cover" />
                    {formData.cover_image_index === index && (
                      <Badge className="absolute top-2 left-2 bg-primary">Cover</Badge>
                    )}
                    <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="secondary" className="h-6 w-6" onClick={() => moveImage(index, -1, 'project')} disabled={index === 0}>
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="secondary" className="h-6 w-6" onClick={() => moveImage(index, 1, 'project')} disabled={index === formData.image_urls.length - 1}>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>
                    {isEditable && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {formData.cover_image_index !== index && (
                          <Button size="sm" variant="secondary" onClick={() => handleSetCover(index, 'project')}>
                            Set Cover
                          </Button>
                        )}
                        <Button size="sm" variant="destructive" onClick={() => handleRemoveImage(index, 'project')}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ================================================================ */}
          {/* ================================================================ */}
          {/* WORKFLOW TAB */}
          {/* ================================================================ */}
          <TabsContent value="updates" className="space-y-5">
            <ProjectUpdatesTab projectId={project.id} />
            <ProjectExitResultsPanel projectId={project.id} onSaved={fetchProject} />
          </TabsContent>

          <TabsContent value="workflow" className="space-y-6">
            <ProjectStatusWorkflow
              projectId={projectId}
              projectTitle={project.title}
              onChanged={fetchProject}
            />

            {/* Audit Information */}
            <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
              <h3 className="font-semibold mb-4">Audit Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Created</span>
                  <span>{project.created_at ? new Date(project.created_at).toLocaleString() : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Last Updated</span>
                  <span>{project.updated_at ? new Date(project.updated_at).toLocaleString() : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Last Published</span>
                  <span>{project.last_published_at ? new Date(project.last_published_at).toLocaleString() : 'Never'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Published Version</span>
                  <span>{project.current_published_version || 'None'}</span>
                </div>
              </div>
            </Card>

            {/* Danger Zone */}
            <Card className="bg-red-50 p-6 rounded-xl border border-red-200">
              <h3 className="font-semibold mb-2 text-red-800">Danger Zone</h3>
              <p className="text-sm text-red-700 mb-4">
                Deleting a project will also delete all related records including instruments, subscriptions, 
                ownership positions, and finance entries. Assets will be unlinked but not deleted. This action cannot be undone.
              </p>
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteDialog(true)}
                data-testid="delete-project-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Project
              </Button>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Sticky Edit Footer */}
        {editMode && (
          <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-black/[0.08] py-4 -mx-4 px-4 mt-6">
            <div className="max-w-7xl mx-auto">
              <Card className="bg-amber-50 border-amber-200 p-4 rounded-xl">
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <Label className="text-amber-700 font-medium">Reason for Changes *</Label>
                    <Input
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      placeholder="Describe why these changes are being made (min 5 characters)"
                      className="mt-2 bg-white"
                      data-testid="edit-reason-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setEditMode(false); fetchProject(); }}>
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-project-btn-footer">
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* ================================================================== */}
      {/* ASSET DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAssetId ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
            <DialogDescription>
              {editingAssetId ? 'Update property details' : 'Create a new property asset and link it to this project'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="location">Location</TabsTrigger>
              <TabsTrigger value="characteristics">Characteristics</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                <strong>Note:</strong> City and Country are required fields - found in the <span className="font-semibold">Location</span> tab.
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Asset Name</Label>
                <Input value={assetFormData.name} onChange={(e) => setAssetFormData({ ...assetFormData, name: e.target.value })} placeholder="e.g., Alfama Heritage Building" className="mt-1 h-9 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Property Use *</Label>
                  <Select value={assetFormData.property_use} onValueChange={(v) => setAssetFormData({ ...assetFormData, property_use: v })}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {propertyUseOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Property Form *</Label>
                  <Select value={assetFormData.property_form} onValueChange={(v) => setAssetFormData({ ...assetFormData, property_form: v })}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {propertyFormOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(assetFormData.property_use === 'other' || assetFormData.property_form === 'other') && (
                <div>
                  <Label className="text-xs text-foreground-muted">Other Description</Label>
                  <Input value={assetFormData.other_description} onChange={(e) => setAssetFormData({ ...assetFormData, other_description: e.target.value })} placeholder="Describe the property type..." className="mt-1 h-9 text-sm" />
                </div>
              )}
              <div>
                <Label className="text-xs text-foreground-muted">Description</Label>
                <Textarea value={assetFormData.description} onChange={(e) => setAssetFormData({ ...assetFormData, description: e.target.value })} rows={4} placeholder="Detailed property description..." className="mt-1 h-9 text-sm" data-testid="asset-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Tenure</Label>
                  <Select value={assetFormData.tenure} onValueChange={(v) => setAssetFormData({ ...assetFormData, tenure: v })}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {tenureOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Condition</Label>
                  <Select value={assetFormData.condition} onValueChange={(v) => setAssetFormData({ ...assetFormData, condition: v })}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {conditionOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="location" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">City *</Label>
                  <Input value={assetFormData.city} onChange={(e) => setAssetFormData({ ...assetFormData, city: e.target.value })} placeholder="Lisbon" className="mt-1 h-9 text-sm" data-testid="asset-city-input" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Country Code *</Label>
                  <Input value={assetFormData.country_code} onChange={(e) => setAssetFormData({ ...assetFormData, country_code: e.target.value.toUpperCase() })} placeholder="PT" maxLength={2} className="mt-1 h-9 text-sm" data-testid="asset-country-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Street Address (Line 1)</Label>
                <Input value={assetFormData.address_line1} onChange={(e) => setAssetFormData({ ...assetFormData, address_line1: e.target.value })} placeholder="Full street address" className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Address Line 2</Label>
                <Input value={assetFormData.address_line2} onChange={(e) => setAssetFormData({ ...assetFormData, address_line2: e.target.value })} placeholder="Apartment, suite, unit, etc." className="mt-1 h-9 text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Region/State</Label>
                  <Input value={assetFormData.region} onChange={(e) => setAssetFormData({ ...assetFormData, region: e.target.value })} placeholder="Lisboa" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Postal Code</Label>
                  <Input value={assetFormData.postal_code} onChange={(e) => setAssetFormData({ ...assetFormData, postal_code: e.target.value })} placeholder="1000-001" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Neighborhood</Label>
                  <Input value={assetFormData.neighborhood} onChange={(e) => setAssetFormData({ ...assetFormData, neighborhood: e.target.value })} placeholder="Alfama" className="mt-1 h-9 text-sm" />
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Latitude</Label>
                  <Input type="number" step="any" value={assetFormData.latitude} onChange={(e) => setAssetFormData({ ...assetFormData, latitude: e.target.value })} placeholder="38.7109" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Longitude</Label>
                  <Input type="number" step="any" value={assetFormData.longitude} onChange={(e) => setAssetFormData({ ...assetFormData, longitude: e.target.value })} placeholder="-9.1334" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Public Precision</Label>
                  <Select value={assetFormData.public_location_precision} onValueChange={(v) => setAssetFormData({ ...assetFormData, public_location_precision: v })}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {locationPrecisionOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Registry Reference</Label>
                  <Input value={assetFormData.registry_reference} onChange={(e) => setAssetFormData({ ...assetFormData, registry_reference: e.target.value })} placeholder="Land registry ID" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Cadastral Reference</Label>
                  <Input value={assetFormData.cadastral_reference} onChange={(e) => setAssetFormData({ ...assetFormData, cadastral_reference: e.target.value })} placeholder="Tax/cadastral ID" className="mt-1 h-9 text-sm" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="characteristics" className="space-y-4 mt-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Bedrooms</Label>
                  <Input type="number" value={assetFormData.bedrooms} onChange={(e) => setAssetFormData({ ...assetFormData, bedrooms: e.target.value })} placeholder="3" className="mt-1 h-9 text-sm" data-testid="asset-bedrooms-input" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Bathrooms</Label>
                  <Input type="number" step="0.5" value={assetFormData.bathrooms} onChange={(e) => setAssetFormData({ ...assetFormData, bathrooms: e.target.value })} placeholder="2" className="mt-1 h-9 text-sm" data-testid="asset-bathrooms-input" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Parking Spaces</Label>
                  <Input type="number" value={assetFormData.parking_spaces} onChange={(e) => setAssetFormData({ ...assetFormData, parking_spaces: e.target.value })} placeholder="1" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Floor Number</Label>
                  <Input type="number" value={assetFormData.floor_number} onChange={(e) => setAssetFormData({ ...assetFormData, floor_number: e.target.value })} placeholder="2" className="mt-1 h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Usable Area (m²)</Label>
                  <Input type="number" value={assetFormData.usable_area_m2} onChange={(e) => setAssetFormData({ ...assetFormData, usable_area_m2: e.target.value })} placeholder="120" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Gross Area (m²)</Label>
                  <Input type="number" value={assetFormData.gross_area_m2} onChange={(e) => setAssetFormData({ ...assetFormData, gross_area_m2: e.target.value })} placeholder="150" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">External Area (m²)</Label>
                  <Input type="number" value={assetFormData.external_area_m2} onChange={(e) => setAssetFormData({ ...assetFormData, external_area_m2: e.target.value })} placeholder="20" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Plot Area (m²)</Label>
                  <Input type="number" value={assetFormData.plot_area_m2} onChange={(e) => setAssetFormData({ ...assetFormData, plot_area_m2: e.target.value })} placeholder="500" className="mt-1 h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Floor Count</Label>
                  <Input type="number" value={assetFormData.floor_count} onChange={(e) => setAssetFormData({ ...assetFormData, floor_count: e.target.value })} placeholder="4" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Unit Count</Label>
                  <Input type="number" value={assetFormData.unit_count} onChange={(e) => setAssetFormData({ ...assetFormData, unit_count: e.target.value })} placeholder="8" className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Year Built</Label>
                  <Input type="number" value={assetFormData.year_built} onChange={(e) => setAssetFormData({ ...assetFormData, year_built: e.target.value })} placeholder="1920" className="mt-1 h-9 text-sm" data-testid="asset-year-built-input" />
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Year Renovated</Label>
                  <Input type="number" value={assetFormData.year_renovated} onChange={(e) => setAssetFormData({ ...assetFormData, year_renovated: e.target.value })} placeholder="2020" className="mt-1 h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Occupancy</Label>
                  <Select value={assetFormData.occupancy} onValueChange={(v) => setAssetFormData({ ...assetFormData, occupancy: v })}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {occupancyOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-foreground-muted">Energy Rating</Label>
                    <Input value={assetFormData.energy_rating} onChange={(e) => setAssetFormData({ ...assetFormData, energy_rating: e.target.value })} placeholder="B" className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-foreground-muted">Energy Scheme</Label>
                    <Input value={assetFormData.energy_scheme} onChange={(e) => setAssetFormData({ ...assetFormData, energy_scheme: e.target.value })} placeholder="EU EPC" className="mt-1 h-9 text-sm" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Amenities</Label>
                <div className="flex flex-wrap gap-2">
                  {amenityOptions.map(amenity => (
                    <Badge
                      key={amenity.value}
                      variant={assetFormData.amenities.includes(amenity.value) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleAmenity(amenity.value)}
                    >
                      {amenity.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="images" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-foreground-muted">Asset Images</Label>
                <Button size="sm" variant="outline" onClick={() => { setImageTarget('asset'); setShowImageDialog(true); }}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Image
                </Button>
              </div>
              {assetFormData.image_urls.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                  <ImageIcon className="h-8 w-8 text-foreground-muted mx-auto mb-2" />
                  <p className="text-foreground-muted text-sm">No images added yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {assetFormData.image_urls.map((url, index) => (
                    <div key={index} className={cn("relative group rounded-lg overflow-hidden border-2", assetFormData.cover_image_index === index ? "border-primary" : "border-transparent")}>
                      <img src={url} alt="" className="w-full h-24 object-cover" />
                      {assetFormData.cover_image_index === index && (
                        <Badge className="absolute top-1 left-1 text-xs px-1">Cover</Badge>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        {assetFormData.cover_image_index !== index && (
                          <Button size="icon" variant="secondary" className="h-6 w-6" onClick={() => handleSetCover(index, 'asset')}>
                            <Star className="h-3 w-3" />
                          </Button>
                        )}
                        <Button size="icon" variant="destructive" className="h-6 w-6" onClick={() => handleRemoveImage(index, 'asset')}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setShowAssetDialog(false); setEditingAssetId(null); }}>Cancel</Button>
            <Button onClick={handleSaveAsset} disabled={saving} data-testid="save-asset-btn">
              {saving ? 'Saving...' : editingAssetId ? 'Save Changes' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* IMAGE DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Image</DialogTitle>
            <DialogDescription>Enter a URL for the image you want to add</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs text-foreground-muted">Image URL</Label>
              <Input value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} placeholder="https://example.com/image.jpg" className="mt-1 h-9 text-sm" data-testid="image-url-input" />
            </div>
            
            {newImageUrl && newImageUrl.startsWith('http') && (
              <div className="border rounded-lg p-2">
                <img src={newImageUrl} alt="Preview" className="w-full h-40 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImageDialog(false); setNewImageUrl(''); }}>Cancel</Button>
            <Button onClick={handleAddImage} data-testid="confirm-add-image-btn">Add Image</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* SPONSOR DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showSponsorDialog} onOpenChange={setShowSponsorDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Sponsor Information</DialogTitle>
            <DialogDescription>Update sponsor/developer profile details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs text-foreground-muted">Sponsor Name</Label>
              <Input value={sponsorFormData.name} onChange={(e) => setSponsorFormData({ ...sponsorFormData, name: e.target.value })} placeholder="Company or individual name" className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-foreground-muted">Description</Label>
              <Textarea value={sponsorFormData.description} onChange={(e) => setSponsorFormData({ ...sponsorFormData, description: e.target.value })} rows={2} placeholder="Brief description..." className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-foreground-muted">Biography</Label>
              <Textarea value={sponsorFormData.biography} onChange={(e) => setSponsorFormData({ ...sponsorFormData, biography: e.target.value })} rows={3} placeholder="Detailed biography..." className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-foreground-muted">Track Record</Label>
              <Textarea value={sponsorFormData.track_record} onChange={(e) => setSponsorFormData({ ...sponsorFormData, track_record: e.target.value })} rows={3} placeholder="Past projects and achievements..." className="mt-1 h-9 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-foreground-muted">Website URL</Label>
                <Input value={sponsorFormData.website} onChange={(e) => setSponsorFormData({ ...sponsorFormData, website: e.target.value })} placeholder="https://..." className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Logo URL</Label>
                <Input value={sponsorFormData.logo_url} onChange={(e) => setSponsorFormData({ ...sponsorFormData, logo_url: e.target.value })} placeholder="https://..." className="mt-1 h-9 text-sm" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSponsorDialog(false)}>Cancel</Button>
            <Button onClick={() => { setShowSponsorDialog(false); toast.success('Sponsor info updated - save project to persist'); }}>
              Update Sponsor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* ASSET PICKER DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showAssetPickerDialog} onOpenChange={setShowAssetPickerDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5" />
              Link Existing Asset
            </DialogTitle>
            <DialogDescription>Select an asset to link to this project. Only unlinked assets are shown.</DialogDescription>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
            <Input 
              value={assetSearchQuery} 
              onChange={(e) => setAssetSearchQuery(e.target.value)} 
              placeholder="Search by name, code, or city..." 
              className="pl-9"
              data-testid="asset-search-input"
            />
          </div>
          
          {/* Asset List */}
          <div className="flex-1 overflow-y-auto mt-4 space-y-2 min-h-[300px] max-h-[400px]">
            {filteredAssets.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-foreground-muted mx-auto mb-4" />
                <h4 className="font-medium mb-2">No Available Assets</h4>
                <p className="text-foreground-muted text-sm">
                  {assetSearchQuery ? 'No assets match your search.' : 'All assets are already linked to projects.'}
                </p>
              </div>
            ) : (
              filteredAssets.map((asset) => (
                <div 
                  key={asset.id} 
                  className="flex items-center justify-between p-4 border border-black/[0.06] rounded-lg hover:border-primary/50 hover:bg-slate-50 transition-colors"
                  data-testid={`asset-picker-item-${asset.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                      {asset.image_urls?.length > 0 ? (
                        <img src={asset.image_urls[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-foreground-muted" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-foreground-muted">{asset.code}</span>
                        <Badge variant="outline" className="text-xs capitalize">{asset.asset_type?.replace('_', ' ') || 'Property'}</Badge>
                      </div>
                      <h4 className="font-medium">{asset.name || 'Unnamed Asset'}</h4>
                      <div className="text-sm text-foreground-muted flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" />
                        {asset.location || `${asset.city || '—'}, ${asset.country_code || '—'}`}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-foreground-muted">
                        {asset.bedrooms != null && (
                          <span className="flex items-center gap-1">
                            <Bed className="h-3 w-3" /> {asset.bedrooms} beds
                          </span>
                        )}
                        {asset.bathrooms != null && (
                          <span className="flex items-center gap-1">
                            <Bath className="h-3 w-3" /> {asset.bathrooms} baths
                          </span>
                        )}
                        {asset.total_area && (
                          <span className="flex items-center gap-1">
                            <Ruler className="h-3 w-3" /> {asset.total_area} m²
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Link Button */}
                  <Button 
                    onClick={() => handleLinkAsset(asset.id)} 
                    disabled={saving}
                    data-testid={`link-asset-btn-${asset.id}`}
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Link
                  </Button>
                </div>
              ))
            )}
          </div>

          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAssetPickerDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* INSTRUMENT DIALOG (shared canonical form) */}
      {/* ================================================================== */}
      <InstrumentFormDialog
        open={showInstrumentDialog}
        onOpenChange={setShowInstrumentDialog}
        instrument={editingInstrument}
        lockedProjectId={project?.id}
        onSaved={fetchProject}
      />

      {/* ================================================================== */}
      {/* INSTRUMENT PICKER DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showInstrumentPickerDialog} onOpenChange={setShowInstrumentPickerDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Link Existing Instrument
            </DialogTitle>
            <DialogDescription>Select an instrument to link to this project. Only unlinked instruments are shown.</DialogDescription>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
            <Input 
              value={instrumentSearchQuery} 
              onChange={(e) => setInstrumentSearchQuery(e.target.value)} 
              placeholder="Search by name or code..." 
              className="pl-9"
              data-testid="instrument-search-input"
            />
          </div>
          
          {/* Instrument List */}
          <div className="flex-1 overflow-y-auto mt-4 space-y-2 min-h-[300px] max-h-[400px]">
            {filteredInstruments.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="h-12 w-12 text-foreground-muted mx-auto mb-4" />
                <h4 className="font-medium mb-2">No Available Instruments</h4>
                <p className="text-foreground-muted text-sm">
                  {instrumentSearchQuery ? 'No instruments match your search.' : 'All instruments are already linked to projects.'}
                </p>
              </div>
            ) : (
              filteredInstruments.map((instrument) => (
                <div 
                  key={instrument.id} 
                  className="flex items-center justify-between p-4 border border-black/[0.06] rounded-lg hover:border-primary/50 hover:bg-slate-50 transition-colors"
                  data-testid={`instrument-picker-item-${instrument.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="h-6 w-6 text-primary" />
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-foreground-muted">{instrument.code}</span>
                        <Badge variant="outline" className="text-xs capitalize">{instrument.security_class}</Badge>
                        <Badge 
                          className={cn(
                            "text-xs",
                            instrument.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          )}
                        >
                          {instrument.status || 'draft'}
                        </Badge>
                      </div>
                      <h4 className="font-medium">{instrument.name}</h4>
                      <div className="flex items-center gap-4 mt-2 text-xs text-foreground-muted">
                        <span>{instrument.total_authorized_units?.toLocaleString() || 0} units</span>
                        <span>{instrument.currency} {instrument.unit_price_at_issuance}/unit</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Link Button */}
                  <Button 
                    onClick={() => handleLinkInstrument(instrument.id)} 
                    disabled={saving}
                    data-testid={`link-instrument-btn-${instrument.id}`}
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Link
                  </Button>
                </div>
              ))
            )}
          </div>

          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowInstrumentPickerDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finance Entry Dialog - shared with the Finance Reports page */}
      <FinanceEntryFormDialog
        open={showFinanceDialog}
        onOpenChange={setShowFinanceDialog}
        entry={editingFinanceEntry}
        projectId={projectId}
        projectType={formData.strategy}
        onSave={() => { setShowFinanceDialog(false); fetchFinanceEntries(); }}
      />

      {/* ================================================================== */}
      {/* DELETE CONFIRMATION DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Delete Project
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{project?.title}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700 font-medium mb-2">
                This will permanently delete:
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                <li>The project and all its data</li>
                <li>All linked instruments/securities</li>
                <li>All subscriptions for this project</li>
                <li>All ownership positions</li>
                <li>All cap table events</li>
                <li>All finance entries (budgets & actuals)</li>
              </ul>
              <p className="text-sm text-red-600 mt-2">
                <strong>Note:</strong> Assets will be unlinked but preserved.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-foreground-muted">Reason for deletion *</Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Please explain why this project is being deleted..."
                rows={3}
                data-testid="delete-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setDeleteReason('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteProject}
              disabled={saving || deleteReason.length < 5}
              data-testid="confirm-delete-btn"
            >
              {saving ? 'Deleting...' : 'Delete Project & Related Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* STATUS CHANGE DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Change Project Status</DialogTitle>
            <DialogDescription>
              Change status from <strong>{statusConfig[project?.status]?.label || project?.status}</strong> to{' '}
              <strong>{statusConfig[selectedNewStatus]?.label || selectedNewStatus}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center gap-4">
              <Badge className={cn('text-sm px-3 py-1', statusConfig[project?.status]?.color)}>
                {statusConfig[project?.status]?.label || project?.status}
              </Badge>
              <span className="text-foreground-muted">→</span>
              <Badge className={cn('text-sm px-3 py-1', statusConfig[selectedNewStatus]?.color)}>
                {statusConfig[selectedNewStatus]?.label || selectedNewStatus}
              </Badge>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-foreground-muted">Reason for status change *</Label>
              <Textarea
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                placeholder="Please explain why this status change is being made..."
                rows={3}
                data-testid="status-change-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowStatusDialog(false);
              setStatusChangeReason('');
              setSelectedNewStatus('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleStatusChange}
              disabled={saving || statusChangeReason.length < 5}
              data-testid="confirm-status-change-btn"
            >
              {saving ? 'Changing...' : 'Change Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
